// BackpackButton.jsx
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { ButtonId } from '../resource/mixpanel.js';
import getNavigationIcon, {
  NavigationId,
} from '../resource/getNavigationIcon.js';

import { color } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import HamburgerMenuItemText from '../component/HamburgerMenuItemText.jsx';

const LOCAL_CACHE_SECONDS = 60; // TODO: remote config

export const BackpackButton = ({
  iconSize = 28,
  backpackFetchedTimestamp = 0,
  dailyRewardStatusFetchedTimestamp = 0,
  hasExpireSoonItem = false,
  shouldUseActiveIcon = false,
  isBackpackModalOpen = false,
  isFetchingBackpackItems = false,
  isFetchingBackpackDailyRewardStatus = false,
  canReceiveDailyReward = false,
  isAuthed = false,
  isMenuAction = false,
  text = '',
  openBackpackModal = () => null,
  fetchBackpackItems = () => null,
  fetchDailyRewardStatus = () => null,
}) => {
  useEffect(() => {
    const shouldFetchNewData =
      Date.now() - backpackFetchedTimestamp >= LOCAL_CACHE_SECONDS * 1000;
    if (!isFetchingBackpackItems && shouldFetchNewData) {
      fetchBackpackItems();
    }
  }, [isFetchingBackpackItems, backpackFetchedTimestamp, fetchBackpackItems]);

  useEffect(() => {
    const shouldFetchNewData =
      Date.now() - dailyRewardStatusFetchedTimestamp >=
      LOCAL_CACHE_SECONDS * 1000;

    if (!isFetchingBackpackDailyRewardStatus && shouldFetchNewData) {
      fetchDailyRewardStatus();
    }
  }, [
    isAuthed,
    isFetchingBackpackDailyRewardStatus,
    dailyRewardStatusFetchedTimestamp,
    fetchDailyRewardStatus,
  ]);

  return (
    <StyledBackpackButton
      data-element_id={ButtonId.Backpack.ButtonBackpack}
      onClick={openBackpackModal}
      isMenuAction={isMenuAction}
    >
      <IconTextWrap>
        <img
          src={getNavigationIcon({
            navigationId: NavigationId.BACKPACK,
            isMatch: shouldUseActiveIcon && isBackpackModalOpen,
          })}
          alt="Backpack"
          width={iconSize}
          height={iconSize}
        />
        {isMenuAction && <HamburgerMenuItemText>{text}</HamburgerMenuItemText>}
      </IconTextWrap>

      {(hasExpireSoonItem || canReceiveDailyReward) && (
        <RedNotificationCircle
          isMenuAction={isMenuAction}
          isDefaultIconSize={iconSize === 28}
        />
      )}
    </StyledBackpackButton>
  );
};

BackpackButton.propTypes = {
  iconSize: PropTypes.number,
  backpackFetchedTimestamp: PropTypes.number,
  dailyRewardStatusFetchedTimestamp: PropTypes.number,
  hasExpireSoonItem: PropTypes.bool,
  shouldUseActiveIcon: PropTypes.bool,
  isBackpackModalOpen: PropTypes.bool,
  isFetchingBackpackItems: PropTypes.bool,
  isFetchingBackpackDailyRewardStatus: PropTypes.bool,
  canReceiveDailyReward: PropTypes.bool,
  isAuthed: PropTypes.bool,
  isMenuAction: PropTypes.bool,
  text: PropTypes.string,
  openBackpackModal: PropTypes.func,
  fetchBackpackItems: PropTypes.func,
  fetchDailyRewardStatus: PropTypes.func,
};

const StyledBackpackButton = styled.button`
  ${ResetButtonStyle}
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  color: white;
  align-items: center;
  justify-content: ${({ isMenuAction }) =>
    isMenuAction ? 'space-between' : 'center'};
  > img {
    object-fit: contain;
  }
`;

const RedNotificationCircle = styled.div`
  width: ${({ isMenuAction, isDefaultIconSize }) =>
    isMenuAction ? '6px' : `${isDefaultIconSize ? 6 : 10}px`};
  height: ${({ isMenuAction, isDefaultIconSize }) =>
    isMenuAction ? '6px' : `${isDefaultIconSize ? 6 : 10}px`};
  position: ${({ isMenuAction }) => (isMenuAction ? 'static' : 'absolute')};
  ${({ isMenuAction, isDefaultIconSize }) =>
    isMenuAction
      ? ''
      : `top: ${isDefaultIconSize ? 6 : 2}px; right: ${isDefaultIconSize ? 6 : 4}px;`}
  background-color: ${color.red};
  border-radius: 50%;
`;

const IconTextWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export default BackpackButton;
